import { useEffect, useRef, useState } from 'react';
import { VERSION } from '../../version';
import { readBool, WELCOME_KEY, writeBool } from '../prefs';
import { THEMES, type ThemeId } from '../theme';
import { Brand } from './Brand';

interface Props {
  theme: ThemeId;
  onTheme: (t: ThemeId) => void;
  onHelp: () => void;
  onAbout: () => void;
}

/** Top bar: brand block (version, copyright, feedback) with ⚙ Settings, ? Help and ⓘ About. */
export function AppBar({ theme, onTheme, onHelp, onAbout }: Props) {
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!wrap.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <header className="flex flex-wrap items-start gap-x-6 gap-y-1 border-b border-line bg-panel px-3 py-1.5">
      <div ref={wrap} className="relative flex flex-col leading-tight">
        <Brand as="h1" />
        <div className="meta-line">v{VERSION}</div>
        <div className="meta-line">© {new Date().getFullYear()} Marian Fujara</div>
        <div className="meta-line">
          <a href="mailto:marian.fujara@gmail.com">feedback</a>
        </div>
        <div className="mt-1 flex items-center gap-1">
          <button
            type="button"
            className="icon-btn text-[16px]"
            aria-label="Settings"
            title="Settings"
            aria-expanded={open}
            onClick={() => setOpen((o) => !o)}
          >
            ⚙
          </button>
          <button
            type="button"
            className="icon-btn"
            aria-label="Help — user manual"
            title="Help — user manual (?)"
            onClick={onHelp}
          >
            ?
          </button>
          <button
            type="button"
            className="icon-btn"
            aria-label="About SID2MIDI Web"
            title="About SID2MIDI Web"
            onClick={onAbout}
          >
            ⓘ
          </button>
        </div>
        {open && (
          <div className="popover" role="group" aria-label="Settings">
            <label className="flex items-center justify-between gap-3">
              <span className="label whitespace-nowrap">Colour scheme</span>
              <select
                className="field max-w-[150px]"
                value={theme}
                onChange={(e) => onTheme(e.target.value as ThemeId)}
              >
                {THEMES.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex items-center justify-between gap-3">
              <span className="label">Welcome message on startup</span>
              <input
                type="checkbox"
                defaultChecked={readBool(WELCOME_KEY, true)}
                onChange={(e) => writeBool(WELCOME_KEY, e.target.checked)}
              />
            </label>
          </div>
        )}
      </div>
      <p className="hidden self-center text-[12px] text-muted md:block">
        Convert C64 SID tunes into editable MIDI for your DAW.
        <span className="ml-3 whitespace-nowrap">🔒 Files never leave your browser.</span>
      </p>
    </header>
  );
}
